import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { JobService } from "@/services/jobService";
import { Input } from "@/components/ui/Input";
import { ArrowLeft, Check, CheckCircle2, Upload, X } from "lucide-react";
import StepPersonal from "@/pages/user/confirm-details/components/steps/StepPersonal";
import { ALTERNATE_EMAIL_COPY } from "@/pages/user/confirm-details/components/steps/alternateEmailCopy";
import StepSkills from "@/pages/user/confirm-details/components/steps/StepSkills";
import StepExperience from "@/pages/user/confirm-details/components/steps/StepExperience";
import StepEducation from "@/pages/user/confirm-details/components/steps/StepEducation";
import StepSalary from "@/pages/user/confirm-details/components/steps/StepSalary";
import StepMoreInfo from "@/pages/user/confirm-details/components/steps/StepMoreInfo";
import ResumePreview from "@/pages/admin/candidate-analysis/edit-profile/components/ResumePreview";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { CandidateService } from "@/services/candidateService";
import RHFInput from "@/components/form/RHFInput";
import { loadDefaultCurrency, getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";
import ResumeValidationModal from "@/components/shared/ResumeValidationModal";
import FilePreview from "@/components/shared/FilePreview";
import { MAX_FILE_UPLOAD } from "@/config/constants";
import { mergeRequiredLanguageRows, requiredLanguageNames, toLanguageStrings } from "@/utils/languageRequirements";
import { cn } from "@/lib/utils";
import { SplitReviewLayout, ReviewFormPane, ReviewStatusChip, TOOLBAR_BUTTON } from "@/components/shared/SplitReviewLayout";
import { buildReuploadSubmitPayload } from "./buildSubmitPayload";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import { reuploadConsentNote } from "../constants/consentModeNotes";
import ReviewConfirmBox from "./components/ReviewConfirmBox";

const SECTIONS = [
  { key: "personal", label: "Personal Info" },
  { key: "skills", label: "Skills & Language" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "salary", label: "Salary & Availability" },
  { key: "moreinfo", label: "More Info" },
];

// SENT  — two-factor: the candidate was emailed a confirmation link and the
//         re-upload only takes effect once they click it.
// APPLIED — single-factor (the job has its re-upload email step off): the
//         resume on file has already been replaced and re-matching is queued.
const STAGE = { UPLOAD: "upload", REVIEW: "review", SENT: "sent", APPLIED: "applied" };

/** The section heading inside a review card. */
const ReviewToggle = ({ label }) => <h3 className="text-base font-semibold text-text-primary mb-3">{label}</h3>;

const ReuploadResume = () => {
  const { candidateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setHeader = useOutletContext();
  const sectionRefs = useRef({});

  const jobId = location.state?.jobId;
  const applicationId = location.state?.applicationId;
  const jobTitle = location.state?.jobTitle;
  const candidateName = location.state?.candidateName;
  const [resolvedCandidateName, setResolvedCandidateName] = useState(candidateName || "");
  const [extractedCandidateName, setExtractedCandidateName] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [pendingReupload, setPendingReupload] = useState(false);
  // Whether this job still emails the candidate a confirmation link, resolved
  // server-side on the status read below. Null until that read lands, and
  // after one that failed — the screen then says nothing rather than
  // promising the wrong mode.
  const [emailConsentRequired, setEmailConsentRequired] = useState(null);
  const [currencySign, setCurrencySign] = useState(getDefaultCurrencyFallback().currencySign);

  useEffect(() => {
    if (jobId) {
      Promise.all([JobService.fetchJobById(jobId), loadDefaultCurrency()])
        .then(([res]) => {
          if (res?.data && res.data.currency_sign !== undefined) {
            setCurrencySign(res.data.currency_sign || getDefaultCurrencyFallback().currencySign);
          }
          setJobRequiredLanguages(res?.data?.languages_required || []);
        })
        .catch((err) => console.error("Failed to fetch job details:", err));
    }
  }, [jobId]);

  useEffect(() => {
    let cancelled = false;

    if (!candidateId || !jobId) return;

    CandidateService.fetchAnalysis({ candidateId, jobId })
      .then((res) => {
        if (cancelled) return;
        const candidateDetails = res?.candidate_details ?? {};
        const tabsData = res?.tabs_details?.resume_confirmed_data ?? {};
        const name = candidateDetails?.candidate_name || candidateDetails?.full_name || tabsData?.personal_information?.full_name || candidateName || "";

        setResolvedCandidateName(name);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to refresh candidate header details:", err);
        setResolvedCandidateName(candidateName || "");
      });

    return () => {
      cancelled = true;
    };
  }, [candidateId, jobId, candidateName]);

  const [stage, setStage] = useState(STAGE.UPLOAD);
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [reuploadId, setReuploadId] = useState(null);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  // The job's own language rows — prefilled first and locked in the editor,
  // same as the candidate-facing apply flow (see `languagesSlot` below).
  const [jobRequiredLanguages, setJobRequiredLanguages] = useState([]);
  const [emailMasked, setEmailMasked] = useState("");
  const [alternateEmailMasked, setAlternateEmailMasked] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [consentText, setConsentText] = useState("");
  // True once a Submit attempt has been blocked by an unreviewed section —
  // flips each still-unreviewed section's box red until it's checked off.
  const [showReviewHint, setShowReviewHint] = useState(false);
  const fileInputRef = useRef(null);

  const [reviewed, setReviewed] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.key, false])));
  const allReviewed = SECTIONS.every((s) => reviewed[s.key]);
  const reviewedCount = SECTIONS.filter((s) => reviewed[s.key]).length;
  // Set by the jump bar, cleared after two seconds.
  const [highlightedSection, setHighlightedSection] = useState(null);

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      consent: false,
    },
  });

  const consentChecked = methods.watch("consent");

  // Which mode the recruiter is attesting to, shown beside the attestation
  // itself rather than reported afterwards.
  const consentNote = reuploadConsentNote(emailConsentRequired);

  // Own the preview object URL here, exactly like the working bulk-review and
  // edit-profile pages do. ResumePreview can also create the URL itself from a
  // bare File, but that path re-creates/revokes it inside its own load effect,
  // which is fragile across re-renders. With the URL owned by this page it is
  // created once per picked file and revoked only when the file changes or the
  // page unmounts.
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreviewFile((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return null;
      });
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    setPreviewFile((prev) => {
      if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
      return { filename: file.name, type: file.type, blob: file, objectUrl };
    });
  }, [file]);

  const handleDownloadPreview = useCallback(() => {
    const url = previewFile?.objectUrl;
    if (!url || !file) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
  }, [previewFile, file]);

  useEffect(() => {
    // Only meaningful on the upload step (clear a picked file if the TA
    // unticks consent before extracting) — scoped to STAGE.UPLOAD so it
    // can't fire off the back of an unrelated form reset once the file has
    // already been used for a successful extraction.
    if (stage === STAGE.UPLOAD && !consentChecked && file) {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [stage, consentChecked, file]);
  const [validating, setValidating] = useState(false);
  const [validationModal, setValidationModal] = useState({ open: false, result: null });
  // The exact File instance already cleared by validation (passed, or the TA
  // chose Continue Anyway). If extraction fails and the TA retries with the
  // SAME file, this skips re-validating it — validation must not repeat once
  // a decision has been made.
  const approvedFileRef = useRef(null);

  const runExtract = useCallback(async () => {
    setExtracting(true);
    try {
      // The attestation is validated server-side now, in both consent modes —
      // pass the actual ticked state, never a hardcoded true.
      const res = await CandidateService.reuploadResumeExtract(candidateId, applicationId, file, consentChecked);
      const data = res?.draft_data || {};

      methods.reset({
        // reset() replaces the ENTIRE form value set — omitting `consent`
        // here made RHF report it as undefined right after a successful
        // extract, which tripped the "clear file if consent unticked" effect
        // above and wiped `file` (and with it the preview) the instant the
        // review stage rendered.
        consent: consentChecked,
        personal_information: {
          name: data?.personal_information?.full_name || "",
          email: data?.personal_information?.email || "",
          // Prefilled from the extract response's `previous_alternate_email` —
          // the address already on file for this application, not the draft.
          alternate_email: res?.previous_alternate_email || "",
          phone: data?.personal_information?.phone || "",
          location: data?.personal_information?.location || "",
          linkedin: data?.personal_information?.linkedin || "",
          portfolio: data?.personal_information?.website || "",
          summary: data?.summary || "",
        },
        // Rows for the paired dropdowns — job-required rows first and
        // locked, then whatever the resume itself reported, same as the
        // apply flow.
        skills: { list: data?.skills || [], languages: mergeRequiredLanguageRows(jobRequiredLanguages, data?.languages) },
        experience: (data?.work_experience || []).map((exp) => ({
          company: exp.company || "",
          jobTitle: exp.title || "",
          startDate: exp.start_date || "",
          endDate: exp.end_date || "",
          location: exp.location || "",
          description: exp.description || "",
          achievements: exp.achievements || [],
        })),
        education: (data?.education || []).map((edu) => ({
          institution: edu.institution || "",
          degree: edu.degree || "",
          fieldOfStudy: edu.field_of_study || "",
          startDate: edu.start_date || "",
          endDate: edu.end_date || "",
          grade: edu.grade || "",
        })),
        compensation: {
          current_salary: "",
          expected_salary: "",
          notice_period: "",
          total_experience: "",
        },
        additionalSections: [],
        social: { instagram_url: "", facebook_url: "", twitter_url: "" },
      });

      setExtractedCandidateName(data?.personal_information?.full_name || "");

      setMandatoryFields(res?.mandatory_fields || []);
      setReuploadId(res?.reupload_id);
      setStage(STAGE.REVIEW);
    } catch (err) {
      console.error("Resume reupload extract failed:", err);
      toast.error(err?.response?.data?.message || "Failed to process resume.");
    } finally {
      setExtracting(false);
    }
  }, [file, consentChecked, applicationId, candidateId, jobRequiredLanguages, methods]);

  const handleExtract = useCallback(async () => {
    if (!file || !consentChecked || !applicationId) return;
    if (validating) return;

    // Already cleared this exact file (validated OK, or Continue Anyway was
    // chosen) — a retry after a failed extraction must not re-validate it.
    if (approvedFileRef.current === file) {
      await runExtract();
      return;
    }

    setValidating(true);
    try {
      const result = await CandidateService.validateResume(file, jobId);
      if (result?.is_valid === false) {
        setValidationModal({ open: true, result });
        return;
      }
      approvedFileRef.current = file;
    } catch (err) {
      // Fail open — the pre-extraction check must never block the upload.
      console.error("Resume validation error:", err);
      approvedFileRef.current = file;
    } finally {
      setValidating(false);
    }

    await runExtract();
  }, [file, consentChecked, applicationId, jobId, validating, runExtract]);

  const handleValidationGoBack = () => {
    setValidationModal({ open: false, result: null });
    approvedFileRef.current = null;
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleValidationContinueAnyway = async () => {
    approvedFileRef.current = file;
    setValidationModal({ open: false, result: null });
    await runExtract();
  };

  const toggleReviewed = (key) => setReviewed((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!applicationId) {
      setCheckingStatus(false);
      return;
    }
    CandidateService.getResumeReuploadStatus(candidateId, applicationId)
      .then((res) => {
        if (res?.has_pending) setPendingReupload(res);
        if (typeof res?.require_reupload_email_consent === "boolean") {
          setEmailConsentRequired(res.require_reupload_email_consent);
        }
      })
      .catch((err) => console.error("Failed to check reupload status:", err))
      .finally(() => setCheckingStatus(false));
  }, [candidateId, applicationId]);

  useEffect(() => {
    CandidateService.getReuploadResumeConsentText()
      .then((res) => setConsentText(res?.text || ""))
      .catch((err) => {
        console.error("Failed to fetch reupload consent text:", err);
        setConsentText(
          "I confirm I have authorization to re-process this candidate's resume with AI for recruitment and hiring purposes, subject to human review before any changes take effect.",
        );
      });
  }, []);

  useEffect(() => {
    const candidateLabel = stage === STAGE.APPLIED ? extractedCandidateName || resolvedCandidateName || candidateName : resolvedCandidateName || candidateName;

    const parts = [candidateLabel, jobTitle].filter(Boolean);
    setHeader({
      title: parts.length ? `Reupload Resume — ${parts.join(" · ")}` : "Reupload Resume",
    });
  }, [stage, extractedCandidateName, resolvedCandidateName, candidateName, jobTitle, jobId, setHeader]);

  const replaceEmptyStringsWithNull = (obj) => {
    if (obj === "") return null;

    if (Array.isArray(obj)) {
      return obj.map(replaceEmptyStringsWithNull);
    }

    if (obj && typeof obj === "object") {
      return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, replaceEmptyStringsWithNull(value)]));
    }

    return obj;
  };

  const onSubmit = methods.handleSubmit(async (formData) => {
    setSubmitting(true);
    try {
      const payload = buildReuploadSubmitPayload(formData);

      const normalizedPayload = replaceEmptyStringsWithNull(payload);

      const res = await CandidateService.reuploadResumeSubmit(candidateId, reuploadId, normalizedPayload);

      if (res?.email_confirmation_required !== false) {
        setEmailMasked(res?.email_masked || "");
        setAlternateEmailMasked(res?.alternate_email_masked || "");
        setStage(STAGE.SENT);
      } else {
        // res.applied === true — the resume on file has already been replaced
        // and re-matching is queued. The candidate detail page refetches on
        // mount (and listens for the reupload socket event), so leaving via
        // "Back to candidate" picks up the reset match score.
        setStage(STAGE.APPLIED);
      }
    } catch (err) {
      console.error("Resume reupload submit failed:", err);
      toast.error(err?.response?.data?.message || "Failed to send confirmation.");
    } finally {
      setSubmitting(false);
    }
  });

  // Reviewed sections keep an emerald ring so progress reads at a glance; the
  // blue ring is the jump-bar highlight, and fades on its own.
  const sectionCardClass = (key) =>
    `transition-all duration-500 rounded-xl ${highlightedSection === key ? "ring-2 ring-tone-blue/50" : reviewed[key] ? "ring-2 ring-tone-emerald/40" : ""}`;

  // Jump to a section still missing its review checkbox and focus it, so the
  // reviewer lands exactly where they need to act instead of just reading a
  // toast and having to hunt for the right section — mirrors the confirm-details
  // wizard's focusConfirmCheckbox behavior.
  // The jump bar's target. Highlights the section for a moment so the eye
  // lands on it, same as the bulk review screen.
  const scrollToSection = useCallback((key) => {
    const el = sectionRefs.current[key];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedSection(key);
    setTimeout(() => setHighlightedSection((current) => (current === key ? null : current)), 2000);
  }, []);

  const focusReviewCheckbox = (key) => {
    sectionRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      document.getElementById(`review-${key}`)?.focus();
    }, 300);
  };

  // Every "Back to candidate" on this page funnels through here. Pop the real
  // history entry when there is one: the details page sits directly behind
  // this route (Details → Reupload), so navigate(-1) restores the exact stack
  // the user expects — one Back from Details reaches the list, and no
  // reupload copy lingers behind Details to loop or duplicate. Replacing here
  // stacked a second identical Details entry, which made the first Back press
  // look like it did nothing. The explicit fallback covers a directly-opened
  // URL (new tab / bookmark), where there is nothing in-app to pop.
  const backToCandidate = useCallback(() => {
    const idx = window.history.state?.idx;
    if (Number.isInteger(idx) && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(`/admin/candidate-analysis/${candidateId}?job_id=${jobId}`, { replace: true });
  }, [candidateId, jobId, navigate]);

  const handleSubmitClick = () => {
    const firstUnreviewed = SECTIONS.find((s) => !reviewed[s.key]);
    if (firstUnreviewed) {
      const missingLabels = SECTIONS.filter((s) => !reviewed[s.key]).map((s) => s.label);
      toast.error(
        missingLabels.length === 1
          ? `Please review "${missingLabels[0]}" before submitting.`
          : `Please review the following sections before submitting: ${missingLabels.join(", ")}.`,
      );
      setShowReviewHint(true);
      focusReviewCheckbox(firstUnreviewed.key);
      return;
    }
    onSubmit();
  };

  if (checkingStatus) {
    return (
      <div className="mx-auto mt-6 px-4">
        <Card className="p-6 text-sm text-text-secondary">Checking reupload status…</Card>
      </div>
    );
  }

  const handleDrop = (e) => {
    e.preventDefault();
    if (!consentChecked) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // STAGE.REVIEW
  return (
    <FormProvider {...methods}>
      {checkingStatus ? (
        <div className="mx-auto mt-6 px-4">
          <Card className="p-6 text-sm text-text-secondary">Checking reupload status…</Card>
        </div>
      ) : pendingReupload?.status === "AWAITING_CONFIRMATION" ? (
        <div className="mx-auto mt-6 max-w-2xl px-4">
          <Card className="p-8 text-center space-y-5">
            <CheckCircle2 className="mx-auto text-amber-500" size={42} />

            <div>
              <h2 className="text-xl font-semibold text-text-primary">Confirmation Pending</h2>

              <p className="mt-2 text-sm text-text-secondary">
                The updated resume has already been reviewed and submitted. A confirmation email has been sent to the candidate.
              </p>
            </div>

            {pendingReupload.email_masked && (
              <div className="rounded-lg bg-background-secondary p-4">
                <p className="text-sm text-text-secondary">Waiting for confirmation from</p>

                <p className="mt-1 font-medium text-text-primary">
                  {pendingReupload.email_masked}
                  {pendingReupload.alternate_email_masked ? ` and ${pendingReupload.alternate_email_masked}` : ""}
                </p>
              </div>
            )}

            {pendingReupload.expires_at && (
              <p className="text-xs text-text-secondary">This confirmation request expires on {new Date(pendingReupload.expires_at).toLocaleString()}</p>
            )}

            {pendingReupload.can_resend && (
              <p className="text-xs text-text-secondary">
                The candidate can request a new link from their confirmation email
                {typeof pendingReupload.sends_remaining === "number"
                  ? ` (${pendingReupload.sends_remaining} resend${pendingReupload.sends_remaining === 1 ? "" : "s"} left).`
                  : "."}
              </p>
            )}

            <Button
              onClick={backToCandidate}
              className="rounded-full bg-button-secondary text-text-base"
            >
              Back to Candidate
            </Button>
          </Card>
        </div>
      ) : stage === STAGE.UPLOAD ? (
        <div className="mx-auto mt-6 space-y-3 px-4 max-w-4xl">
          {/* Same back button the review stage's toolbar uses. */}
          <Button
            type="button"
            onClick={backToCandidate}
            className="flex h-8 w-fit shrink-0 items-center gap-1 rounded-lg border border-border-primary bg-background-tertiary px-3 py-0 text-xs text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft size={14} /> Back to candidate
          </Button>

          <Card className="bg-background-secondary border-0 md:border md:border-border-primary md:p-4 p-0 rounded-md">
            <CardHeader className="p-0 items-start">
              <h1 className="text-text-primary font-semibold text-base mb-1">Resume</h1>
              <p className="text-text-secondary md:text-[12px] text-xs mb-3 leading-relaxed">
                Upload a resume that's different from the candidate's current one on file.
              </p>
            </CardHeader>

            {/* Consent checkbox — the exact markup the apply flow uses for its
                AI consent (user/apply/index.jsx), so both resume-upload
                surfaces look and behave the same. Bound to RHF via setValue;
                the watched `consentChecked` still drives the gates below. */}
            <label
              htmlFor="reupload-consent"
              className={cn(
                "flex items-start gap-3 mb-3 p-3 rounded-lg border transition-colors",
                extracting ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                consentChecked ? "border-blue-500/50 bg-blue-500/5" : "border-border-primary hover:border-gray-500",
              )}
            >
              <div className="relative flex-shrink-0 mt-0.5">
                <Input
                  type="checkbox"
                  id="reupload-consent"
                  disabled={extracting}
                  checked={consentChecked}
                  onChange={(e) => methods.setValue("consent", e.target.checked)}
                  className="peer sr-only"
                />
                <div
                  className={cn(
                    "w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                    consentChecked ? "bg-[#3580d7] border-[#3580d7]" : "bg-transparent border-gray-500",
                  )}
                >
                  {consentChecked && <Check size={10} strokeWidth={3} className="text-white" />}
                </div>
              </div>

              <div className="flex flex-col">
                <span className="text-text-primary text-[12px] leading-snug">{consentText}</span>
                {consentNote && <span className="text-text-secondary text-[11px] leading-snug mt-1">{consentNote}</span>}
              </div>
            </label>

            {/* File Upload Section */}
            <label
              htmlFor="reupload-file-input"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={cn(
                "block border border-dashed rounded-lg p-3 text-center bg-background-primary transition-all duration-200",
                !consentChecked || extracting ? "border-gray-700 opacity-50 cursor-not-allowed" : "border-gray-600 hover:border-gray-500 cursor-pointer",
              )}
            >
              <Input
                ref={fileInputRef}
                id="reupload-file-input"
                type="file"
                accept=".pdf,.doc,.docx"
                disabled={!consentChecked || extracting}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />

              <div className="flex justify-center mb-2 text-text-primary">
                <div className="w-8 h-8 rounded-full bg-background-secondary border border-border-primary flex items-center justify-center">
                  <Upload className="w-4 h-4" />
                </div>
              </div>

              <p className="text-text-primary text-xs">Drop your resume or click to browse</p>
              <span className="text-text-blue text-xs">Click to browse</span>
            </label>

            {/* Supported File Types and Size */}
            <div className="flex justify-between mt-3 mb-2 text-[10px] text-text-secondary">
              <span>Supported: PDF, DOC, and DOCX</span>
              <span>Maximum Size: {MAX_FILE_UPLOAD} MB</span>
            </div>

            {/* Selected File Preview */}
            {file && (
              <div className="flex items-center gap-3 rounded-lg px-3 py-3 mt-2 border border-border-primary min-w-0">
                <div className="flex-shrink-0">
                  <FilePreview file={file} />
                </div>

                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-text-primary text-sm font-medium truncate">{file.name}</p>
                  <p className="text-text-secondary text-xs mt-0.5">
                    {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                  </p>
                </div>

                <Button
                  type="button"
                  disabled={extracting}
                  onClick={() => setFile(null)}
                  className="text-text-secondary transition-colors p-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-secondary"
                >
                  <X size={18} />
                </Button>
              </div>
            )}
          </Card>

          <div className="flex justify-end gap-3 mt-3 mb-3">
            <Button
              disabled={!file || !consentChecked || extracting || validating}
              onClick={handleExtract}
              className={cn(TOOLBAR_BUTTON, "bg-button-secondary text-text-base")}
            >
              {validating ? "Checking resume..." : extracting ? "Extracting..." : "Extract"}
            </Button>
          </div>

          <ResumeValidationModal
            open={validationModal.open}
            fileName={file?.name}
            validation={validationModal.result}
            mode="individual"
            onGoBack={handleValidationGoBack}
            onContinueAnyway={handleValidationContinueAnyway}
          />
        </div>
      ) : stage === STAGE.SENT ? (
        <Card className="max-w-xl mx-auto mt-10 p-8 text-center space-y-3">
          <CheckCircle2 className="mx-auto text-green-500" size={40} />
          <h2 className="text-lg font-semibold text-text-primary">Confirmation email sent</h2>
          <p className="text-sm text-text-secondary">
            A confirmation link has been sent to the candidate
            {emailMasked ? ` (${[emailMasked, alternateEmailMasked].filter(Boolean).join(" and ")})` : ""}. Once they confirm, the updated resume replaces the current one
            and matching restarts automatically.
          </p>
          <Button onClick={backToCandidate} className="rounded-full bg-button-secondary px-5 py-2 text-text-base">
            Back to candidate
          </Button>
        </Card>
      ) : stage === STAGE.APPLIED ? (
        <Card className="max-w-xl mx-auto mt-10 p-8 text-center space-y-3">
          <CheckCircle2 className="mx-auto text-green-500" size={40} />
          <h2 className="text-lg font-semibold text-text-primary">Updated resume applied</h2>
          <p className="text-sm text-text-secondary">
            The updated resume has replaced the one on file. It's being matched against the job again — the new match score will appear on the candidate's page shortly.
          </p>
          <Button
            onClick={backToCandidate}
            className="rounded-full bg-button-secondary px-5 py-2 text-text-base"
          >
            Back to candidate
          </Button>
        </Card>
      ) : (
        <SplitReviewLayout
          onBack={backToCandidate}
          backLabel="Back to candidate"
          backDisabled={submitting}
          left={
            <ReviewFormPane
              sections={SECTIONS}
              onSectionClick={scrollToSection}
              footer={
                <div className="flex flex-wrap items-center gap-2">
                  <ReviewStatusChip label={`${reviewedCount} of ${SECTIONS.length} sections reviewed`} tone={allReviewed ? "emerald" : "amber"} />

                  <Button disabled={submitting} onClick={handleSubmitClick} className={cn(TOOLBAR_BUTTON, "ml-auto bg-button-secondary text-text-base")}>
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              }
            >
              <Card ref={(el) => (sectionRefs.current.personal = el)} className={sectionCardClass("personal")}>
                <ReviewToggle label="Personal Info" />
                <StepPersonal
                  mandatoryFields={mandatoryFields}
                  alternateEmailLabel={ALTERNATE_EMAIL_COPY.reupload.label}
                  alternateEmailHelperText={ALTERNATE_EMAIL_COPY.reupload.helperText}
                />
                <ReviewConfirmBox sectionKey="personal" reviewed={reviewed} showReviewHint={showReviewHint} onToggle={toggleReviewed} />
              </Card>

              <Card ref={(el) => (sectionRefs.current.skills = el)} className={sectionCardClass("skills")}>
                <ReviewToggle label="Skills & Language" />
                <StepSkills
                  mandatoryFields={mandatoryFields}
                  languagesSlot={(disabled, required) => (
                    <LanguageProficiencyRows
                      name="skills.languages"
                      disabled={disabled}
                      emptyText="No languages added."
                      allowUnknownProficiency
                      lockedLanguages={requiredLanguageNames(jobRequiredLanguages)}
                      languageRequired={required?.language}
                      levelledLanguages={jobRequiredLanguages}
                    />
                  )}
                />
                <ReviewConfirmBox sectionKey="skills" reviewed={reviewed} showReviewHint={showReviewHint} onToggle={toggleReviewed} />
              </Card>

              <Card ref={(el) => (sectionRefs.current.experience = el)} className={sectionCardClass("experience")}>
                <ReviewToggle label="Experience" />
                <StepExperience mandatoryFields={mandatoryFields} autoCalculate={false} />
                <ReviewConfirmBox sectionKey="experience" reviewed={reviewed} showReviewHint={showReviewHint} onToggle={toggleReviewed} />
              </Card>

              <Card ref={(el) => (sectionRefs.current.education = el)} className={sectionCardClass("education")}>
                <ReviewToggle label="Education" />
                <StepEducation mandatoryFields={mandatoryFields} />
                <ReviewConfirmBox sectionKey="education" reviewed={reviewed} showReviewHint={showReviewHint} onToggle={toggleReviewed} />
              </Card>

              <Card ref={(el) => (sectionRefs.current.salary = el)} className={sectionCardClass("salary")}>
                <ReviewToggle label="Salary & Availability" />
                <StepSalary mandatoryFields={mandatoryFields} salaryFieldsRequired={false} autoCalculate showSalaryDetails={false} currencySign={currencySign} />
                <ReviewConfirmBox sectionKey="salary" reviewed={reviewed} showReviewHint={showReviewHint} onToggle={toggleReviewed} />
              </Card>

              <Card ref={(el) => (sectionRefs.current.moreinfo = el)} className={sectionCardClass("moreinfo")}>
                <ReviewToggle label="More Info" />
                <StepMoreInfo showSocials={false} mandatoryFields={mandatoryFields} />
                <ReviewConfirmBox sectionKey="moreinfo" reviewed={reviewed} showReviewHint={showReviewHint} onToggle={toggleReviewed} />
              </Card>
            </ReviewFormPane>
          }
          right={<ResumePreview file={previewFile} onDownload={handleDownloadPreview} />}
        />
      )}
    </FormProvider>
  );
};

export default ReuploadResume;
